<template>
  <div>
    <Field
      v-bind="$attrs"
      :id="props.id"
      v-model="checked"
      type="checkbox"
      :name="props.name"
      :value="true"
      :class="$style.base"
      :rules="validator"
      @change="onChange"
    />
    <label
      :for="id"
      :class="[$style.checkboxLabel, checked ? $style.checkedLabel : {}]"
    >
      <slot />
    </label>
    <ErrorMessage
      :class="[$style.errorMessage]"
      :name="props.name"
    />
  </div>
</template>
<script setup lang="ts">
import { watch, computed } from 'vue'
import { useField, Field, ErrorMessage } from 'vee-validate'

const props = defineProps({
  id: { type: String, default: '' },
  name: { type: String, required: true },
  disabled: { type: Boolean, required: false, default: false },
  required: { type: Boolean, required: false, default: false },
  modelValue: {
    type: Boolean,
    required: true,
    default: false
  },
  errorMessage: { type: String, default: 'Diese Angabe ist verpflichtend' }
})

const emits = defineEmits(['click', 'update:modelValue'])

const validator = (): string | boolean => {
  if (props.required) {
    if (checked.value === true) {
      return true
    }
    return props.errorMessage
  }
  return true
}

const { validate } = useField<boolean>(props.name, validator)

watch(() => props.required, validate.bind(null))

const checked = computed({
  get () {
    return props.modelValue
  },
  set (newValue) {
    emits('update:modelValue', newValue ?? false)
  }
})

const onChange = (event: Event) => {
  emits('click', event)
}
</script>

<script lang="ts">
export default {
  inheritAttrs: false
}
</script>

<style module>
.base {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.checkboxLabel {
  cursor: pointer;
  position: relative;
  top: 0;
  left: 0;
  padding: 0 0 0 32px;
  display: block;
  min-height: 27px;
  opacity: 1;
  font-size: clamp(14px, 2vw, 16px);
  line-height: clamp(17px, 2vw, 19px);

  &::before {
    position: absolute;
    top: 3px;
    left: 0;
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-pacificblue);
    border-radius: var(--radius-default);
    content: '';
    transition: var(--transition-default);
  }
}

.checkedLabel {
  &::before {
    background: var(--color-pacificblue);
  }

  &::after {
    font-family: var(--font-family-icomoon);
    font-size: 14px;
    line-height: 24px;
    width: 24px;
    text-align: center;
    content: var(--icon-check);
    position: absolute;
    top: 3px;
    left: 0;
    color: var(--color-white);
  }
}

.errorMessage {
  margin-left: 32px;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.18px;
  font-weight: bold;
}
</style>
